import PortalIdParser from 'PortalIdParser';
import { useContext, useEffect } from 'react';
// @ts-ignore Untyped module
import EditorConfigSingleton from 'ContentEditorUI/EditorConfigSingleton';
import { getIntentSpecificContext } from 'copilot-plugins/utils/contentAssistantUtils';
import { useSelector } from 'react-redux';
import { useSetCopilotFreeContext } from 'copilot-toolkit/hooks/useSetCopilotFreeContext';
// @ts-ignore Untyped selector
import { getPreviewIframeUrl } from 'ContentEditorUI/redux/selectors/iframesSelectors';
import CopilotCollaborationContextProvider from 'tinymce-copilot/components/CopilotCollaborationContextProvider';
// @ts-ignore Untyped selector
import Bus from 'ContentEditorUI/coffee/lib/eventBus';
import { forwardCommandToCopilot } from 'tinymce-copilot/handleSlashOrHighlightCommand';
// @ts-ignore Untyped selector
import { getHost } from '../redux/selectors/routingSelectors';
import { getLanguage } from 'ContentEditorUI/redux/selectors/baseContentModelSelectors';
import { getAbsoluteUrl } from 'ContentEditorUI/redux/selectors/contentReadOnlyDataSelectors';
// @ts-ignore Untyped selector
import { getCanUseCopilotCollaborationMinusContentAssistanceCheck } from 'ContentEditorUI/redux/selectors/featureSelectors';
import { useCurrentContentContext } from '../utils/contentContextUtils';
import useClearCopilotActiveEditorOnModuleChange from './useClearCopilotActiveEditorOnModuleChange';
// @ts-ignore Untyped
import EditorActionsAndEventsContext from '../context/EditorActionsAndEventsContext';
import { jsx as _jsx } from "react/jsx-runtime";
const SETTINGS_PATH = `/settings/${PortalIdParser.get()}/ai-assistant`;
export function ContentEditorCopilotCollaborationContextProvider({
  children
}) {
  const canUseCopilotCollaboration = useSelector(getCanUseCopilotCollaborationMinusContentAssistanceCheck);
  const appKey = EditorConfigSingleton.get('appKey');
  const hostUrl = useSelector(getHost);
  const settingsLink = `${hostUrl}${SETTINGS_PATH}`;
  const contentLanguage = useSelector(getLanguage);
  const previewIframeUrl = useSelector(getPreviewIframeUrl);
  const contentAbsoluteUrl = useSelector(getAbsoluteUrl);
  const setCopilotFreeContext = useSetCopilotFreeContext();

  // @ts-ignore Use global to ensure we immediately get the contentId on first render
  const {
    contentId
  } = window.hubspot.parseEditorUrl();
  useEffect(() => {
    const freeContext = {
      contentId
    };
    if (contentAbsoluteUrl) {
      freeContext.contentAbsoluteUrl = contentAbsoluteUrl;
    }
    if (previewIframeUrl && !previewIframeUrl.includes('PLACEHOLDER')) {
      freeContext.previewIframeUrl = previewIframeUrl;
    }
    setCopilotFreeContext(freeContext);
  }, [contentId, contentAbsoluteUrl, previewIframeUrl, setCopilotFreeContext]);
  return /*#__PURE__*/_jsx(CopilotCollaborationContextProvider, {
    appKey: appKey,
    isCopilotCollaborationEnabled: canUseCopilotCollaboration,
    sessionId: contentId,
    hostUrl: hostUrl,
    settingsLink: settingsLink,
    contentLanguage: contentLanguage,
    customHandleTextReplacement: customHandleTextReplacement,
    useCustomCollaborationSessionLogic: useCustomCollaborationSessionLogic,
    children: children
  });
}

// Further custom editor logic that needs to use sendActionFromAppToCopilot and sendCollaborationStateChangeFromAppToCopilot
// from useCollaborationSession
function useCustomCollaborationSessionLogic(sendActionFromAppToCopilot, sendCollaborationStateChangeFromAppToCopilot, currentContextRef) {
  const {
    bus
  } = useContext(EditorActionsAndEventsContext);
  useClearCopilotActiveEditorOnModuleChange();
  const targetSelector = 'body';
  const {
    currentContext,
    getCurrentContentContext
  } = useCurrentContentContext(targetSelector);
  useEffect(() => {
    if (!currentContext && bus.ready) {
      getCurrentContentContext();
    }
  }, [bus.ready, currentContext, getCurrentContentContext]);
  useEffect(() => {
    // On first render, set the current context
    // Even if tinyMCE has not yet loaded, we can still set the editorText
    if (currentContext) {
      currentContextRef.current = {
        editorText: currentContext
      };
    }
  }, [currentContext, currentContextRef]);
  useEffect(() => {
    bus.on('post:copilot:send-copilot-command', message => {
      const {
        command,
        editorContext,
        currentSelection,
        hasSelection,
        precedingText = '',
        followingText = '',
        brandVoiceOptions,
        contentLanguage
      } = message.payload;
      forwardCommandToCopilot(command, editorContext, hasSelection, currentSelection, precedingText, followingText, currentContextRef, sendActionFromAppToCopilot, brandVoiceOptions, contentLanguage);
    });
    bus.on('post:copilot:update-tiny-mce-state', message => {
      const {
        hasFocus,
        hasSelection,
        currentSelection,
        precedingText = '',
        followingText = '',
        contentLanguage = 'en',
        editorContext
      } = message.payload;
      const collaborationContext = getIntentSpecificContext({
        editorText: editorContext,
        precedingText,
        followingText,
        text: currentSelection,
        contentLanguage
      });
      currentContextRef.current = collaborationContext;
      // Send the current state to Copilot
      sendCollaborationStateChangeFromAppToCopilot({
        hasFocus,
        hasSelection
      });
    });
    return () => {
      bus.off('post:copilot:send-copilot-command');
      bus.off('post:copilot:update-tiny-mce-state');
    };
  }, [bus, currentContextRef, getCurrentContentContext, sendActionFromAppToCopilot, sendCollaborationStateChangeFromAppToCopilot]);
}
function customHandleTextReplacement(activeEditor, generatedText) {
  if (activeEditor) {
    // If there is an active editor in the parent frame, fall through to default behavior
    return false;
  }

  // Note: using the postMessage not in the EditorActionsAndEventsContext.
  // This isn't a hook - and for the case, it doesn't matter - as its just sending a message
  Bus.postMessage({
    action: 'insertIntoRichText:inpage',
    payload: {
      text: generatedText
    }
  });
}